import { datadogRum, DataDogSeverityLevel } from '@/lib/DatadogService';
import { baseQueryType } from '@/resiliency/util';
import {
  BaseQueryError,
  BaseQueryFn,
  FetchArgs,
  BaseQueryApi,
  BaseQueryExtraOptions,
  RetryOptions,
  FetchBaseQueryError,
  retry
} from '@reduxjs/toolkit/query';

/**
 * Enhances a base query function with automatic retry logic based on configurable error codes and retry limits.
 *
 * The retry behavior is controlled by environment variables:
 * - `VITE_RESILIENCY_ERROR_STATUSES`: A comma-separated list of error codes (string or number) that should trigger a retry.
 * - `VITE_API_RETRY_LIMIT`: The maximum number of retry attempts allowed.
 *
 * The function applies a custom backoff strategy, tracks exceptions using Application Insights,
 * and determines whether to retry based on the error status and current attempt count.
 *
 * @param baseQuery - The base query function to wrap with retry logic.
 * @returns A new base query function with retry and backoff capabilities.
 *
 * @remarks
 * - Retries are only attempted if the error status matches one of the configured error codes and the retry limit has not been exceeded.
 * - Exception tracking is performed for failed retries and unexpected errors.
 */
export const baseQueryWithRetry = (baseQuery: baseQueryType): baseQueryType => {
  const errorCodesForRetry: (string | number)[] =
    import.meta.env.VITE_RESILIENCY_ERROR_STATUSES?.split(',')?.map((a: string) =>
      !isNaN(Number(a)) ? parseInt(a) : a
    );
  const retry_limit = import.meta.env.VITE_API_RETRY_LIMIT
    ? parseInt(import.meta.env.VITE_API_RETRY_LIMIT)
    : 0;

  const customBackOff = async (attempt = 0, maxRetries = retry_limit) => {
    const attempts = Math.min(attempt, maxRetries);

    await new Promise(resolve => {
      const timeout = setTimeout(resolve, attempts * 1000);
      return () => clearTimeout(timeout);
    });
  };

  const trackException = (
    error: Error,
    attempt: number,
    url?: string,
    level: DataDogSeverityLevel = DataDogSeverityLevel.Error
  ) => {
    datadogRum.addError(error, {
      severityLevel: level,
      attempt,
      retry_limit,
      url
    });
  };

  const shouldRetry = (
    error: BaseQueryError<BaseQueryFn>,
    args: FetchArgs,
    extraArgs: {
      attempt: number;
      baseQueryApi: BaseQueryApi;
      extraOptions: BaseQueryExtraOptions<BaseQueryFn> & RetryOptions;
    }
  ) => {
    const url = (args as FetchArgs)?.url;
    try {
      if (retry_limit <= 0 || extraArgs?.baseQueryApi?.type !== 'query') return false;

      if (extraArgs.attempt > retry_limit) {
        trackException(
          new Error('[Retry Policy] Attempted all retries.'),
          extraArgs.attempt,
          url,
          DataDogSeverityLevel.Warning
        ); // warning
        return false;
      }

      return errorCodesForRetry?.includes((error as FetchBaseQueryError)?.status ?? '');
    } catch (ex) {
      trackException(ex as Error, extraArgs.attempt, url, DataDogSeverityLevel.Error); // Error
    }

    return false;
  };

  return retry(baseQuery, {
    backoff: customBackOff,
    retryCondition: shouldRetry
  });
};
